import { useState, useEffect, useRef } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertToRaw, convertFromHTML } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

import Sidebar from '../components/Sidebar';
import { 
  Menu, 
  SlidersHorizontal, 
  Search, 
  Plus, 
  MoreVertical,
  Edit,
  Trash2,
  X,
  Download,
  ChevronDown,
  FileText,
  DollarSign,
  Calendar,
  CheckCircle,
  XCircle,
  Loader2,
  EyeClosed,
  EyeOff
} from 'lucide-react';

const BASE_URL = 'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/plans';

function getAuthHeaders() {
  // Use the access_token issued for the backend API; avoid id_token/token keys
  const token = (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('access_token'))
    || (typeof localStorage !== 'undefined' && localStorage.getItem('access_token'));
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedPlans, setSelectedPlans] = useState([]);
  const [showAddPlan, setShowAddPlan] = useState(false);
  const [showEditPlan, setShowEditPlan] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false); // New state for form submission
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [currentFontSize, setCurrentFontSize] = useState(14);
  const [resources, setResources] = useState([]);
  const [activeTab, setActiveTab] = useState('details');
  const [showViewPlan, setShowViewPlan] = useState(false);
  const [viewingPlan, setViewingPlan] = useState(null);
  const [overlayMessage, setOverlayMessage] = useState('');
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);


const defaultBenefits = {
    time_credit: { amount: 0, unit: 'minutes', applicable_resources: [], neverExpire: false, expireInDays: 30 },
    money_credit: { amount: 0, applicable_resources: [], neverExpire: false, expireInDays: 30 },
    passes: {
      count: 0,
      is_unlimited: false,
      access_window: {
        days: [],
        start_time: '',
        end_time: ''
      },
      neverExpire: false,
      expireInDays: 30
    },
    printing_credits: { amount: 0, neverExpire: false, expireInDays: 30 }
  };
  
  const [newPlan, setNewPlan] = useState({
    name: '',
    privateOfficeNo: '',
    price: '',
    every: '24',
    period: 'months',
    discount: 'no',
    discountPercent: '20',
    description: '',
    benefits: defaultBenefits
  });

  const formatCurrency = (amount) => {
    const cleanAmount = typeof amount === 'string' ? amount.replace(/[^0-9.-]+/g, "") : amount;
    
    const formattedNumber = parseFloat(cleanAmount || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `IDR ${formattedNumber}`;
  };

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}/getPlan`, { 
        headers: { Accept: 'application/json', ...getAuthHeaders() } 
      });
      const data = await response.json();
      
      const plansArray = Array.isArray(data.plans) ? data.plans : [];
      
      const formattedPlans = plansArray.map(plan => ({
        id: plan._id,
        name: plan.name,
        price: `IDR ${Number(plan.price ?? 0)}`,
        frequency: `every ${Number(plan.durationMonths ?? 1)} ${Number(plan.durationMonths ?? 1) === 1 ? 'month' : 'months'}`,
        status: Boolean(plan.active) ? 'Published' : 'Internal',
        description: plan.description || '',
        discountPercent: Number(plan.discountPercent ?? 0),
        durationMonths: Number(plan.durationMonths ?? 1),
        rawData: plan
      }));
      
      setPlans(formattedPlans);
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchResources = async () => {
    try {
      const response = await fetch('https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/resource/viewAllResource', { 
        headers: { Accept: 'application/json', ...getAuthHeaders() } 
      });
      const data = await response.json();
      setResources(data.data || []);
    } catch (error) {
      console.error('Error fetching resources:', error);
    }
  };

  useEffect(() => {
    fetchPlans();
    fetchResources();
  }, []);

  const handleBulkToggle = () => {
    setBulkMode(!bulkMode);
    setSelectedPlans([]);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedPlans(filteredPlans.map(plan => plan.id));
    } else {
      setSelectedPlans([]);
    }
  };

  const handleSelectPlan = (planId) => {
    setSelectedPlans(prev => 
      prev.includes(planId) 
        ? prev.filter(id => id !== planId)
        : [...prev, planId]
    );
  };

  const handleDeleteSelected = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedPlans.length} plan(s)?`)) {
      return;
    }
  
    try {
      setSubmitting(true);
      for (const planId of selectedPlans) {
        await fetch(`${BASE_URL}/deletePlan/${planId}`, { 
          method: 'PATCH',
          headers: { Accept: 'application/json', ...getAuthHeaders() } 
        });
      }
      
      await fetchPlans();
      setSelectedPlans([]);
      setBulkMode(false);
      showOverlay('Selected plans deleted successfully');
    } catch (error) {
      console.error('Error deleting plans:', error);
      alert('Failed to delete plans');
    } finally {
      setSubmitting(false);
    }
  };

  const handleExport = () => {
    const header = [
      'Name',
      'Price',
      'Frequency',
      'Status',
      'Description',
      'Discount enabled',
      'Discount %'
    ];
    
    const rows = plans.map(plan => {
      const rawDescription = plan.description || '';
      const plainDescription = typeof rawDescription === 'string'
        ? rawDescription.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
        : '';
    
      const discountEnabled = plan.discountPercent && Number(plan.discountPercent) > 0;
      const discountPercent = discountEnabled ? Number(plan.discountPercent) : 0;
    
      return [
        plan.name,
        plan.price,
        plan.frequency,
        plan.status,
        plainDescription,
        discountEnabled ? 'Yes' : 'No',
        `${discountPercent}`
      ];
    });

    const csvContent = [header, ...rows]
      .map(row => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `plans-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleAddPlan = async () => {
    try {
      setSubmitting(true);
      let durationMonths = parseInt(newPlan.every);
      if (newPlan.period === 'years') {
        durationMonths = durationMonths * 12;
      }
  
      const planData = {
        name: newPlan.name,
        privateOfficeNo: newPlan.privateOfficeNo,
        price: parseFloat(newPlan.price || 0),
        description: newPlan.description,
        billingCycle: 'monthly',
        durationMonths: durationMonths,
        discountPercent: newPlan.discount === 'yes' ? parseInt(newPlan.discountPercent) : 0,
        benefits: newPlan.benefits,
      };

      console.log('Creating plan with data:', planData);
  
      const response = await fetch(`${BASE_URL}/createPlan`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          Accept: 'application/json', 
          ...getAuthHeaders() 
        },
        body: JSON.stringify(planData)
      });
  
      if (!response.ok) {
        let msg = 'Failed to create plan';
        try { 
          const errorData = await response.json();
          msg = errorData.message || msg; 
        } catch {
          try { msg = await response.text() || msg; } catch {}
        }
        throw new Error(msg);
      }
  
      await fetchPlans();
      setShowAddPlan(false);
      resetForm();
      showOverlay('Plan created successfully');
    } catch (error) {
      console.error('Error creating plan:', error);
      alert('Failed to create plan: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditPlan = async () => {
    try {
      setSubmitting(true);
      let durationMonths = parseInt(newPlan.every);
      if (newPlan.period === 'years') {
        durationMonths = durationMonths * 12;
      }
  
      const planData = {
        name: newPlan.name,
        privateOfficeNo: newPlan.privateOfficeNo,
        price: parseFloat(newPlan.price || 0),
        description: newPlan.description,
        billingCycle: 'monthly',
        durationMonths: durationMonths,
        discountPercent: newPlan.discount === 'yes' ? parseInt(newPlan.discountPercent) : 0,
        active: editingPlan.status === 'Published',
        benefits: newPlan.benefits,
      };
  
      const response = await fetch(`${BASE_URL}/updatePlan/${editingPlan.id}`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json', 
          Accept: 'application/json', 
          ...getAuthHeaders() 
        },
        body: JSON.stringify(planData)
      });
  
      if (!response.ok) {
        let msg = 'Failed to update plan';
        try { 
          const errorData = await response.json();
          msg = errorData.message || msg; 
        } catch {
          try { msg = await response.text() || msg; } catch {}
        }
        throw new Error(msg);
      }
  
      await fetchPlans();
      setShowEditPlan(false);
      setEditingPlan(null);
      resetForm();
      showOverlay('Plan updated successfully');
    } catch (error) {
      console.error('Error updating plan:', error);
      alert('Failed to update plan: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setNewPlan({
      name: '',
      price: '',
      privateOfficeNo: '',
      every: '24',
      period: 'months',
      discount: 'no',
      discountPercent: '20',
      description: '',
      benefits: defaultBenefits
    });
    setEditorState(EditorState.createEmpty());
    setCurrentFontSize(14);
    setActiveTab('details');
  };

  const openEditModal = (plan) => {
    setEditingPlan(plan);
    
    let every = plan.durationMonths;
    let period = 'months';
    
    if (plan.durationMonths >= 12 && plan.durationMonths % 12 === 0) {
      every = plan.durationMonths / 12;
      period = 'years';
    }

    if (plan.description) {
      const blocksFromHTML = convertFromHTML(plan.description);
      if (blocksFromHTML.contentBlocks) {
        const contentState = ContentState.createFromBlockArray(
          blocksFromHTML.contentBlocks,
          blocksFromHTML.entityMap
        );
        setEditorState(EditorState.createWithContent(contentState));
      } else {
        setEditorState(EditorState.createEmpty());
      }
    } else {
      setEditorState(EditorState.createEmpty());
    }

    // Helper to safely extract IDs just in case the backend populated the objects
    const extractIds = (arr) => Array.isArray(arr) ? arr.map(item => typeof item === 'object' && item !== null ? (item.id || item._id) : item) : [];

    const rawBenefits = plan.rawData?.benefits || {};
    
    // Safely deep merge to guarantee arrays exist for the checkboxes
    const safeBenefits = {
      time_credit: {
        ...defaultBenefits.time_credit,
        ...(rawBenefits.time_credit || {}),
        applicable_resources: extractIds(rawBenefits.time_credit?.applicable_resources)
      },
      money_credit: {
        ...defaultBenefits.money_credit,
        ...(rawBenefits.money_credit || {}),
        applicable_resources: extractIds(rawBenefits.money_credit?.applicable_resources)
      },
      passes: {
        ...defaultBenefits.passes,
        ...(rawBenefits.passes || {}),
        access_window: {
          ...defaultBenefits.passes?.access_window,
          ...(rawBenefits.passes?.access_window || {})
        }
      },
      printing_credits: {
        ...defaultBenefits.printing_credits,
        ...(typeof rawBenefits.printing_credits === 'object' 
             ? rawBenefits.printing_credits 
             : { amount: rawBenefits.printing_credits || 0 })
      }
    };

    setNewPlan({
      name: plan.name,
      privateOfficeNo: plan.rawData?.privateOfficeNo || '',
      price: plan.price.replace('IDR', '').trim(),
      every: String(every),
      period: period,
      discount: plan.discountPercent > 0 ? 'yes' : 'no',
      discountPercent: String(plan.discountPercent || 20),
      description: plan.description || '',
      benefits: safeBenefits
    });
    setShowEditPlan(true);
    setActiveDropdown(null);
    setActiveTab('details');
  };

  const handleStatusChange = async (planId, newStatus) => {
    try {
      const plan = plans.find(p => p.id === planId);
      if (!plan) return;
  
      const active = newStatus === 'Published';
      
      const response = await fetch(`${BASE_URL}/updatePlan/${planId}`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json', 
          Accept: 'application/json', 
          ...getAuthHeaders() 
        },
        body: JSON.stringify({ active })
      });
  
      if (!response.ok) {
        let msg = 'Failed to update status';
        try { 
          const errorData = await response.json();
          msg = errorData.message || msg; 
        } catch {}
        throw new Error(msg);
      }
  
      await fetchPlans();
      setActiveDropdown(null);
      showOverlay(`Plan ${newStatus === 'Published' ? 'published' : 'set to internal'} successfully`);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status: ' + error.message);
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) {
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/deletePlan/${planId}`, {
        method: 'PATCH',
        headers: {
          Accept: 'application/json',
          ...getAuthHeaders()
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete plan');
      }

      await fetchPlans();
      setActiveDropdown(null);
      showOverlay('Plan deleted successfully');
    } catch (error) {
      console.error('Error deleting plan:', error);
      alert('Failed to delete plan: ' + error.message);
    }
  };

  const updateBenefit = (category, field, value) => {
    setNewPlan(prev => ({
      ...prev,
      benefits: {
        ...prev.benefits,
        [category]: { ...prev.benefits[category], [field]: value }
      }
    }));
  };

  const updatePassWindow = (field, value) => {
    setNewPlan(prev => ({
      ...prev,
      benefits: {
        ...prev.benefits,
        passes: {
          ...prev.benefits.passes,
          access_window: { ...prev.benefits.passes.access_window, [field]: value }
        }
      }
    }));
  };

  const handleResourceToggle = (category, resourceId) => {
    setNewPlan(prev => {
      const currentSelected = prev.benefits[category].applicable_resources || [];
      const isSelected = currentSelected.includes(resourceId);
      return {
        ...prev,
        benefits: {
          ...prev.benefits,
          [category]: {
            ...prev.benefits[category],
            applicable_resources: isSelected 
              ? currentSelected.filter(id => id !== resourceId)
              : [...currentSelected, resourceId]
          }
        }
      };
    });
  };

  const filteredPlans = plans.filter(plan =>
    plan.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ---------- FIX: Separate Editor Selection State from HTML Generation ----------
  const onEditorStateChange = (newEditorState) => {
    setEditorState(newEditorState);
  };

  const onContentStateChange = (contentState) => {
    if (contentState) {
      const html = draftToHtml(contentState);
      setNewPlan(prev => ({ ...prev, description: html }));
    }
  };
  // -----------------------------------------------------------------------------

  const openViewModal = (plan) => {
    setViewingPlan(plan);
    setActiveTab('details');
    setShowViewPlan(true);
  };

  const getResourceName = (id) => {
    const resource = resources.find(r => r.id === id || r._id === id);
    return resource ? resource.name : 'Unknown Resource';
  };

  const handleFontSizeIncrease = () => {
    if (currentFontSize < 48) {
      setCurrentFontSize(prev => prev + 2);
    }
  };

  const handleFontSizeDecrease = () => {
    if (currentFontSize > 8) {
      setCurrentFontSize(prev => prev - 2);
    }
  };

  const isFormValid = () => {
    return newPlan.name.trim() !== '' && 
           newPlan.price !== '';
  };

  const showOverlay = (message) => {
    setOverlayMessage(message);
    setIsOverlayOpen(true);
    setTimeout(() => setIsOverlayOpen(false), 3000);
  };

  // Calculate stats
  const totalPlans = plans.length;
  const publishedPlans = plans.filter(p => p.status === 'Published').length;
  const internalPlans = plans.filter(p => p.status === 'Internal').length;
  const avgPrice = plans.length > 0 
    ? Math.round(plans.reduce((sum, p) => sum + parseFloat(p.price.replace('IDR', '').trim()), 0) / plans.length)
    : 0;

  if (loading) {
    return (
      <div className="flex h-screen overflow-hidden bg-gray-50">
        <Sidebar currentPath="/plans" />
        <div className="flex-1 flex flex-col overflow-hidden">
          <main className="flex-1 overflow-y-auto bg-gray-50 flex items-center justify-center">
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Loading...</span>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar 
        currentPath="/plans" 
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />
      
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* ── Mobile top bar ── */}
        <header className="md:hidden flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200 sticky top-0 z-30">
          <button
            className="p-2 rounded-md text-gray-600 hover:bg-gray-100 transition-colors"
            onClick={() => setMobileSidebarOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <img
            src="/logo.png"
            alt="Optimus Logo"
            className="h-7 w-auto object-contain mr-5"
          />
        </header>

        <main className="flex-1 overflow-y-auto bg-gray-50">
          <div className="p-4 md:p-6">
            {/* Header */}
            <div className="mb-6">
              <h1 className="text-2xl font-semibold text-gray-900">Plans</h1>
              <p className="text-sm text-gray-500 mt-1">
                Manage your membership plans and pricing
              </p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg p-5 shadow-sm border border-gray-200">
                <div className="flex items-start justify-between mb-3">
                  <div className="text-sm text-gray-600">Total Plans</div>
                </div>
                <div className="flex items-end justify-between">
                  <div className="text-3xl font-semibold text-gray-900">
                    {totalPlans}
                  </div>
                  <FileText className="w-5 h-5 text-gray-400" strokeWidth={1.5} />
                </div>
              </div>

              <div className="bg-white rounded-lg p-5 shadow-sm border border-gray-200">
                <div className="flex items-start justify-between mb-3">
                  <div className="text-sm text-gray-600">Published</div>
                </div>
                <div className="flex items-end justify-between">
                  <div className="text-3xl font-semibold text-gray-900">
                    {publishedPlans}
                  </div>
                  <CheckCircle className="w-5 h-5 text-gray-400" strokeWidth={1.5} />
                </div>
              </div>

              <div className="bg-white rounded-lg p-5 shadow-sm border border-gray-200">
                <div className="flex items-start justify-between mb-3">
                  <div className="text-sm text-gray-600">Internal</div>
                </div>
                <div className="flex items-end justify-between">
                  <div className="text-3xl font-semibold text-gray-900">
                    {internalPlans}
                  </div>
                  <XCircle className="w-5 h-5 text-gray-400" strokeWidth={1.5} />
                </div>
              </div>

              <div className="bg-[#214BAE] rounded-lg p-5 shadow-sm cursor-pointer hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="text-sm text-blue-100">Avg. Price (in IDR)</div>
                </div>
                <div className="flex items-center justify-center">
                  <div className="text-3xl font-semibold text-white">
                 {formatCurrency(avgPrice).slice(3)}
                  </div>
                </div>
              </div>
            </div>

            {/* Filters and Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4">
              <div className="p-4">
                <div className="flex items-center justify-between gap-4 overflow-x-auto pb-2">
                  {/* Search */}
                  <div className="relative flex-1 min-w-[200px] sm:min-w-[300px] max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search plans..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2 min-w-max">
                    {bulkMode && selectedPlans.length > 0 && (
                      <button
                        onClick={handleDeleteSelected}
                        disabled={submitting}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Deleting...
                          </>
                        ) : (
                          <>
                            <Trash2 className="w-4 h-4" />
                            Delete ({selectedPlans.length})
                          </>
                        )}
                      </button>
                    )}
                    <button
                      onClick={handleBulkToggle}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <SlidersHorizontal className="w-4 h-4" />
                      {bulkMode ? 'Cancel' : 'Bulk Actions'}
                    </button>
                    <button
                      onClick={handleExport}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Export
                    </button>
                    <button
                      onClick={() => setShowAddPlan(true)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-[#214BAE] rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Add Plan
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Plans Table */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
              <div className="min-w-[900px]">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      {bulkMode && (
                        <th className="px-6 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={selectedPlans.length === filteredPlans.length && filteredPlans.length > 0}
                            onChange={handleSelectAll}
                            className="rounded border-gray-300"
                          />
                        </th>
                      )}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Plan Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Price
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Frequency
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Discount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredPlans.length > 0 ? (
                      filteredPlans.map((plan) => (
                        <tr key={plan.id} onClick={() => openViewModal(plan)} className="hover:bg-gray-50 transition-colors cursor-pointer">
                          {bulkMode && (
                            <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={selectedPlans.includes(plan.id)}
                                onChange={() => handleSelectPlan(plan.id)}
                                className="rounded border-gray-300"
                              />
                            </td>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {plan.name}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{formatCurrency(plan.price)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 capitalize">{plan.frequency}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${
                              plan.discountPercent > 0
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {plan.discountPercent > 0 ? `${plan.discountPercent}% OFF` : 'No Discount'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${
                              plan.status === 'Published'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {plan.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="relative inline-block">
                              <button
                                onClick={() => setActiveDropdown(activeDropdown === plan.id ? null : plan.id)}
                                className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                              >
                                <MoreVertical className="w-5 h-5" />
                              </button>
                              
                              {activeDropdown === plan.id && (
  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[100]">
                                  <button
                                    onClick={() => openEditModal(plan)}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                  >
                                    <Edit className="w-4 h-4" />
                                    Edit Plan
                                  </button>
                                  <button
                                    onClick={() => handleStatusChange(plan.id, plan.status === 'Published' ? 'Internal' : 'Published')}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                  >
                                    {plan.status === 'Published' ? <EyeOff className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                                    {plan.status === 'Published' ? 'Set as Internal' : 'Publish'}
                                  </button>
                                  <button
                                    onClick={() => handleDeletePlan(plan.id)}
                                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    Delete Plan
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={bulkMode ? "7" : "6"} className="px-6 py-12 text-center text-sm text-gray-500">
                          No plans found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

            </div>
            </div>
          </div>
        </main>
      </div>

      {/* Add Plan Modal */}
      {showAddPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold text-gray-900">Add New Plan</h2>
              <button
                onClick={() => {
                  setShowAddPlan(false);
                  resetForm();
                }}
                disabled={submitting}
                className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Tabs Header */}
            <div className="flex border-b border-gray-200 px-6 mt-2">
              <button
                className={`py-2 px-4 border-b-2 font-medium text-sm transition-colors ${activeTab === 'details' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveTab('details')}
              >
                Plan Details
              </button>
              <button
                className={`py-2 px-4 border-b-2 font-medium text-sm transition-colors ${activeTab === 'benefits' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveTab('benefits')}
              >
                Benefits
              </button>
            </div>

            <div className="p-6 space-y-4">
              {activeTab === 'details' ? (
                <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Plan Name *
                      </label>
                      <input
                        type="text"
                        value={newPlan.name}
                        onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })}
                        disabled={submitting}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        placeholder="Enter plan name"
                      />
                    </div>
                    {/* Private Office Number */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Private Office Number
                      </label>
                      <input
                        type="text"
                        value={newPlan.privateOfficeNo}
                        onChange={(e) => setNewPlan({ ...newPlan, privateOfficeNo: e.target.value })}
                        disabled={submitting}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        placeholder="e.g., 101-A (Optional)"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Price (IDR) *
                      </label>
                      <input
                        type="number"
                        value={newPlan.price}
                        onChange={(e) => setNewPlan({ ...newPlan, price: e.target.value })}
                        disabled={submitting}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        placeholder="Enter price"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Every
                        </label>
                        <input
                          type="number"
                          value={newPlan.every}
                          onChange={(e) => setNewPlan({ ...newPlan, every: e.target.value })}
                          disabled={submitting}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Period
                        </label>
                        <select
                          value={newPlan.period}
                          onChange={(e) => setNewPlan({ ...newPlan, period: e.target.value })}
                          disabled={submitting}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <option value="months">Months</option>
                          <option value="years">Years</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Discount
                      </label>
                      <select
                        value={newPlan.discount}
                        onChange={(e) => setNewPlan({ ...newPlan, discount: e.target.value })}
                        disabled={submitting}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                    </div>

                    {newPlan.discount === 'yes' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Discount Percentage
                        </label>
                        <input
                          type="number"
                          value={newPlan.discountPercent}
                          onChange={(e) => setNewPlan({ ...newPlan, discountPercent: e.target.value })}
                          disabled={submitting}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                          placeholder="Enter discount percentage"
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <div className="border border-gray-200 rounded-lg">
                        <Editor
                          editorState={editorState}
                          onEditorStateChange={onEditorStateChange}
                          onContentStateChange={onContentStateChange}
                          toolbar={{
                            options: ['inline', 'blockType', 'list', 'link'],
                            inline: { options: ['bold', 'italic', 'underline'] },
                          }}
                          editorStyle={{ minHeight: '150px', padding: '0 15px' }}
                        />
                      </div>
                    </div>
                  </div>
              ) : (
                <div className="space-y-6">
                  {/* Time Credit */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h3 className="font-semibold text-gray-800 mb-3">Time Credit</h3>
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Amount</label>
                        <input type="number" min="0" className="w-full px-3 py-2 border rounded" 
                          value={newPlan.benefits?.time_credit?.amount || 0} 
                          onChange={e => updateBenefit('time_credit', 'amount', Number(e.target.value))} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Unit</label>
                        <select className="w-full px-3 py-2 border rounded"
                          value={newPlan.benefits?.time_credit?.unit || 'minutes'}
                          onChange={e => updateBenefit('time_credit', 'unit', e.target.value)}>
                          <option value="minutes">Minutes</option>
                          <option value="hours">Hours</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-3 items-end">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Expiry (Days)</label>
                        <input type="number" min="1" disabled={newPlan.benefits?.time_credit?.neverExpire} className="w-full px-3 py-2 border rounded disabled:bg-gray-200" 
                          value={newPlan.benefits?.time_credit?.expireInDays || 0} 
                          onChange={e => updateBenefit('time_credit', 'expireInDays', Number(e.target.value))} />
                      </div>
                      <label className="flex items-center space-x-2 text-sm pb-2">
                        <input type="checkbox" checked={newPlan.benefits?.time_credit?.neverExpire || false} 
                          onChange={e => updateBenefit('time_credit', 'neverExpire', e.target.checked)} />
                        <span>Never Expire</span>
                      </label>
                    </div>
                    <label className="block text-xs text-gray-500 mb-1">Applicable Resources</label>
                    <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 border bg-white rounded">
                      {resources.map(res => (
                        <label key={res.id || res._id} className="flex items-center space-x-2 text-sm">
                          <input type="checkbox"
                            checked={newPlan.benefits?.time_credit?.applicable_resources?.includes(res.id || res._id) || false}
                            onChange={() => handleResourceToggle('time_credit', res.id || res._id)} />
                          <span>{res.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Money Credit */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h3 className="font-semibold text-gray-800 mb-3">Money Credit</h3>
                    <div className="mb-3">
                      <label className="block text-xs text-gray-500 mb-1">Amount (IDR)</label>
                      <input type="number" min="0" className="w-full px-3 py-2 border rounded" 
                        value={newPlan.benefits?.money_credit?.amount || 0} 
                        onChange={e => updateBenefit('money_credit', 'amount', Number(e.target.value))} />
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-3 items-end">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Expiry (Days)</label>
                        <input type="number" min="1" disabled={newPlan.benefits?.money_credit?.neverExpire} className="w-full px-3 py-2 border rounded disabled:bg-gray-200" 
                          value={newPlan.benefits?.money_credit?.expireInDays || 0} 
                          onChange={e => updateBenefit('money_credit', 'expireInDays', Number(e.target.value))} />
                      </div>
                      <label className="flex items-center space-x-2 text-sm pb-2">
                        <input type="checkbox" checked={newPlan.benefits?.money_credit?.neverExpire || false} 
                          onChange={e => updateBenefit('money_credit', 'neverExpire', e.target.checked)} />
                        <span>Never Expire</span>
                      </label>
                    </div>
                    <label className="block text-xs text-gray-500 mb-1">Applicable Resources</label>
                    <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 border bg-white rounded">
                      {resources.map(res => (
                        <label key={res.id || res._id} className="flex items-center space-x-2 text-sm">
                          <input type="checkbox"
                            checked={newPlan.benefits?.money_credit?.applicable_resources?.includes(res.id || res._id) || false}
                            onChange={() => handleResourceToggle('money_credit', res.id || res._id)} />
                          <span>{res.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Passes */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h3 className="font-semibold text-gray-800 mb-3">Passes</h3>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Count</label>
                        <input type="number" min="0" className="w-full px-3 py-2 border rounded" 
                          disabled={newPlan.benefits?.passes?.is_unlimited}
                          value={newPlan.benefits?.passes?.count || 0} 
                          onChange={e => updateBenefit('passes', 'count', Number(e.target.value))} />
                      </div>
                      <div className="flex items-end pb-2">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input type="checkbox" 
                            checked={newPlan.benefits?.passes?.is_unlimited || false}
                            onChange={e => updateBenefit('passes', 'is_unlimited', e.target.checked)} />
                          <span className="text-sm font-medium">Unlimited Passes</span>
                        </label>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-3 items-end">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Expiry (Days)</label>
                        <input type="number" min="1" disabled={newPlan.benefits?.passes?.neverExpire} className="w-full px-3 py-2 border rounded disabled:bg-gray-200" 
                          value={newPlan.benefits?.passes?.expireInDays || 0} 
                          onChange={e => updateBenefit('passes', 'expireInDays', Number(e.target.value))} />
                      </div>
                      <label className="flex items-center space-x-2 text-sm pb-2">
                        <input type="checkbox" checked={newPlan.benefits?.passes?.neverExpire || false} 
                          onChange={e => updateBenefit('passes', 'neverExpire', e.target.checked)} />
                        <span>Never Expire</span>
                      </label>
                    </div>
                    
                    <h4 className="text-sm font-medium text-gray-700 mb-2 mt-4">Access Window</h4>
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Start Time</label>
                        <input type="time" className="w-full px-3 py-2 border rounded" 
                          value={newPlan.benefits?.passes?.access_window?.start_time || ''} 
                          onChange={e => updatePassWindow('start_time', e.target.value)} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">End Time</label>
                        <input type="time" className="w-full px-3 py-2 border rounded" 
                          value={newPlan.benefits?.passes?.access_window?.end_time || ''} 
                          onChange={e => updatePassWindow('end_time', e.target.value)} />
                      </div>
                    </div>
                    <label className="block text-xs text-gray-500 mb-1">Valid Days</label>
                    <div className="flex flex-wrap gap-3 p-2 bg-white border rounded">
                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                        <label key={day} className="flex items-center space-x-1 text-sm">
                          <input type="checkbox" 
                            checked={newPlan.benefits?.passes?.access_window?.days?.includes(day)}
                            onChange={(e) => {
                              const currentDays = newPlan.benefits?.passes?.access_window?.days || [];
                              updatePassWindow('days', e.target.checked 
                                ? [...currentDays, day] 
                                : currentDays.filter(d => d !== day));
                            }}/>
                          <span>{day.substring(0,3)}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Printing Credits */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h3 className="font-semibold text-gray-800 mb-3 text-sm">Printing Credits</h3>
                    <div className="mb-3">
                      <label className="block text-xs text-gray-500 mb-1">Amount</label>
                      <input type="number" min="0" className="w-full px-3 py-2 border rounded" 
                        value={newPlan.benefits?.printing_credits?.amount || 0} 
                        onChange={e => updateBenefit('printing_credits', 'amount', Number(e.target.value))} />
                    </div>
                    <div className="grid grid-cols-2 gap-4 items-end">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Expiry (Days)</label>
                        <input type="number" min="1" 
                          disabled={newPlan.benefits?.printing_credits?.neverExpire} 
                          className="w-full px-3 py-2 border rounded disabled:bg-gray-200" 
                          value={newPlan.benefits?.printing_credits?.expireInDays || 0} 
                          onChange={e => updateBenefit('printing_credits', 'expireInDays', Number(e.target.value))} />
                      </div>
                      <label className="flex items-center space-x-2 text-sm pb-2">
                        <input type="checkbox" 
                          checked={newPlan.benefits?.printing_credits?.neverExpire || false} 
                          onChange={e => updateBenefit('printing_credits', 'neverExpire', e.target.checked)} />
                        <span>Never Expire</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 sticky bottom-0 bg-white">
              <button
                onClick={() => {
                  setShowAddPlan(false);
                  resetForm();
                }}
                disabled={submitting}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleAddPlan}
                disabled={!isFormValid() || submitting}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Adding Plan...
                  </>
                ) : (
                  'Add Plan'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Plan Modal */}
      {showEditPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold text-gray-900">Edit Plan</h2>
              <button
                onClick={() => {
                  setShowEditPlan(false);
                  setEditingPlan(null);
                  resetForm();
                }}
                disabled={submitting}
                className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Tabs Header */}
            <div className="flex border-b border-gray-200 px-6 mt-2">
              <button
                className={`py-2 px-4 border-b-2 font-medium text-sm transition-colors ${activeTab === 'details' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveTab('details')}
              >
                Plan Details
              </button>
              <button
                className={`py-2 px-4 border-b-2 font-medium text-sm transition-colors ${activeTab === 'benefits' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveTab('benefits')}
              >
                Benefits
              </button>
            </div>

            <div className="p-6 space-y-4">
              {activeTab === 'details' ? (
                <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Plan Name *
                      </label>
                      <input
                        type="text"
                        value={newPlan.name}
                        onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })}
                        disabled={submitting}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        placeholder="Enter plan name"
                      />
                    </div>

                    {/* Private Office Number */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Private Office Number
                      </label>
                      <input
                        type="text"
                        value={newPlan.privateOfficeNo}
                        onChange={(e) => setNewPlan({ ...newPlan, privateOfficeNo: e.target.value })}
                        disabled={submitting}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        placeholder="e.g., 101-A (Optional)"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Price (IDR) *
                      </label>
                      <input
                        type="number"
                        value={newPlan.price}
                        onChange={(e) => setNewPlan({ ...newPlan, price: e.target.value })}
                        disabled={submitting}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        placeholder="Enter price"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Every
                        </label>
                        <input
                          type="number"
                          value={newPlan.every}
                          onChange={(e) => setNewPlan({ ...newPlan, every: e.target.value })}
                          disabled={submitting}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Period
                        </label>
                        <select
                          value={newPlan.period}
                          onChange={(e) => setNewPlan({ ...newPlan, period: e.target.value })}
                          disabled={submitting}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <option value="months">Months</option>
                          <option value="years">Years</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Discount
                      </label>
                      <select
                        value={newPlan.discount}
                        onChange={(e) => setNewPlan({ ...newPlan, discount: e.target.value })}
                        disabled={submitting}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                    </div>

                    {newPlan.discount === 'yes' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Discount Percentage
                        </label>
                        <input
                          type="number"
                          value={newPlan.discountPercent}
                          onChange={(e) => setNewPlan({ ...newPlan, discountPercent: e.target.value })}
                          disabled={submitting}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                          placeholder="Enter discount percentage"
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <div className="border border-gray-200 rounded-lg">
                        <Editor
                          editorState={editorState}
                          onEditorStateChange={onEditorStateChange}
                          onContentStateChange={onContentStateChange}
                          toolbar={{
                            options: ['inline', 'blockType', 'list', 'link'],
                            inline: { options: ['bold', 'italic', 'underline'] },
                          }}
                          editorStyle={{ minHeight: '150px', padding: '0 15px' }}
                        />
                      </div>
                    </div>
                  </div>
              ) : (
                <div className="space-y-6">
                  {/* Time Credit */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h3 className="font-semibold text-gray-800 mb-3 text-sm">Time Credit</h3>
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Amount</label>
                        <input type="number" min="0" className="w-full px-3 py-2 border rounded" 
                          value={newPlan.benefits?.time_credit?.amount || 0} 
                          onChange={e => updateBenefit('time_credit', 'amount', Number(e.target.value))} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Unit</label>
                        <select className="w-full px-3 py-2 border rounded"
                          value={newPlan.benefits?.time_credit?.unit || 'minutes'}
                          onChange={e => updateBenefit('time_credit', 'unit', e.target.value)}>
                          <option value="minutes">Minutes</option>
                          <option value="hours">Hours</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-3 items-end">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Expiry (Days)</label>
                        <input type="number" min="1" disabled={newPlan.benefits?.time_credit?.neverExpire} className="w-full px-3 py-2 border rounded disabled:bg-gray-200" 
                          value={newPlan.benefits?.time_credit?.expireInDays || 0} 
                          onChange={e => updateBenefit('time_credit', 'expireInDays', Number(e.target.value))} />
                      </div>
                      <label className="flex items-center space-x-2 text-sm pb-2">
                        <input type="checkbox" checked={newPlan.benefits?.time_credit?.neverExpire || false} 
                          onChange={e => updateBenefit('time_credit', 'neverExpire', e.target.checked)} />
                        <span>Never Expire</span>
                      </label>
                    </div>
                    <label className="block text-xs text-gray-500 mb-1">Applicable Resources</label>
                    <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 border bg-white rounded">
                      {resources.map(res => (
                        <label key={res.id || res._id} className="flex items-center space-x-2 text-sm">
                          <input type="checkbox"
                            checked={newPlan.benefits?.time_credit?.applicable_resources?.includes(res.id || res._id) || false}
                            onChange={() => handleResourceToggle('time_credit', res.id || res._id)} />
                          <span>{res.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Money Credit */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h3 className="font-semibold text-gray-800 mb-3 text-sm">Money Credit</h3>
                    <div className="mb-3">
                      <label className="block text-xs text-gray-500 mb-1">Amount (IDR)</label>
                      <input type="number" min="0" className="w-full px-3 py-2 border rounded" 
                        value={newPlan.benefits?.money_credit?.amount || 0} 
                        onChange={e => updateBenefit('money_credit', 'amount', Number(e.target.value))} />
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-3 items-end">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Expiry (Days)</label>
                        <input type="number" min="1" disabled={newPlan.benefits?.money_credit?.neverExpire} className="w-full px-3 py-2 border rounded disabled:bg-gray-200" 
                          value={newPlan.benefits?.money_credit?.expireInDays || 0} 
                          onChange={e => updateBenefit('money_credit', 'expireInDays', Number(e.target.value))} />
                      </div>
                      <label className="flex items-center space-x-2 text-sm pb-2">
                        <input type="checkbox" checked={newPlan.benefits?.money_credit?.neverExpire || false} 
                          onChange={e => updateBenefit('money_credit', 'neverExpire', e.target.checked)} />
                        <span>Never Expire</span>
                      </label>
                    </div>
                    <label className="block text-xs text-gray-500 mb-1">Applicable Resources</label>
                    <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 border bg-white rounded">
                      {resources.map(res => (
                        <label key={res.id || res._id} className="flex items-center space-x-2 text-sm">
                          <input type="checkbox"
                            checked={newPlan.benefits?.money_credit?.applicable_resources?.includes(res.id || res._id) || false}
                            onChange={() => handleResourceToggle('money_credit', res.id || res._id)} />
                          <span>{res.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Passes */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h3 className="font-semibold text-gray-800 mb-3 text-sm">Passes</h3>
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Count</label>
                        <input type="number" min="0" className="w-full px-3 py-2 border rounded" 
                          disabled={newPlan.benefits?.passes?.is_unlimited}
                          value={newPlan.benefits?.passes?.count || 0} 
                          onChange={e => updateBenefit('passes', 'count', Number(e.target.value))} />
                      </div>
                      <div className="flex items-end pb-2">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input type="checkbox" 
                            checked={newPlan.benefits?.passes?.is_unlimited || false}
                            onChange={e => updateBenefit('passes', 'is_unlimited', e.target.checked)} />
                          <span className="text-sm font-medium">Unlimited Passes</span>
                        </label>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-3 items-end">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Expiry (Days)</label>
                        <input type="number" min="1" disabled={newPlan.benefits?.passes?.neverExpire} className="w-full px-3 py-2 border rounded disabled:bg-gray-200" 
                          value={newPlan.benefits?.passes?.expireInDays || 0} 
                          onChange={e => updateBenefit('passes', 'expireInDays', Number(e.target.value))} />
                      </div>
                      <label className="flex items-center space-x-2 text-sm pb-2">
                        <input type="checkbox" checked={newPlan.benefits?.passes?.neverExpire || false} 
                          onChange={e => updateBenefit('passes', 'neverExpire', e.target.checked)} />
                        <span>Never Expire</span>
                      </label>
                    </div>
                    
                    <h4 className="text-sm font-medium text-gray-700 mb-2 mt-4">Access Window</h4>
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Start Time</label>
                        <input type="time" className="w-full px-3 py-2 border rounded" 
                          value={newPlan.benefits?.passes?.access_window?.start_time || ''} 
                          onChange={e => updatePassWindow('start_time', e.target.value)} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">End Time</label>
                        <input type="time" className="w-full px-3 py-2 border rounded" 
                          value={newPlan.benefits?.passes?.access_window?.end_time || ''} 
                          onChange={e => updatePassWindow('end_time', e.target.value)} />
                      </div>
                    </div>
                    <label className="block text-xs text-gray-500 mb-1">Valid Days</label>
                    <div className="flex flex-wrap gap-3 p-2 bg-white border rounded">
                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                        <label key={day} className="flex items-center space-x-1 text-sm">
                          <input type="checkbox" 
                            checked={newPlan.benefits?.passes?.access_window?.days?.includes(day)}
                            onChange={(e) => {
                              const currentDays = newPlan.benefits?.passes?.access_window?.days || [];
                              updatePassWindow('days', e.target.checked 
                                ? [...currentDays, day] 
                                : currentDays.filter(d => d !== day));
                            }}/>
                          <span>{day.substring(0,3)}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Printing Credits */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h3 className="font-semibold text-gray-800 mb-3 text-sm">Printing Credits</h3>
                    <div className="mb-3">
                      <label className="block text-xs text-gray-500 mb-1">Amount</label>
                      <input type="number" min="0" className="w-full px-3 py-2 border rounded" 
                        value={newPlan.benefits?.printing_credits?.amount || 0} 
                        onChange={e => updateBenefit('printing_credits', 'amount', Number(e.target.value))} />
                    </div>
                    <div className="grid grid-cols-2 gap-4 items-end">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Expiry (Days)</label>
                        <input type="number" min="1" 
                          disabled={newPlan.benefits?.printing_credits?.neverExpire} 
                          className="w-full px-3 py-2 border rounded disabled:bg-gray-200" 
                          value={newPlan.benefits?.printing_credits?.expireInDays || 0} 
                          onChange={e => updateBenefit('printing_credits', 'expireInDays', Number(e.target.value))} />
                      </div>
                      <label className="flex items-center space-x-2 text-sm pb-2">
                        <input type="checkbox" 
                          checked={newPlan.benefits?.printing_credits?.neverExpire || false} 
                          onChange={e => updateBenefit('printing_credits', 'neverExpire', e.target.checked)} />
                        <span>Never Expire</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 sticky bottom-0 bg-white">
              <button
                onClick={() => {
                  setShowEditPlan(false);
                  setEditingPlan(null);
                  resetForm();
                }}
                disabled={submitting}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleEditPlan}
                disabled={!isFormValid() || submitting}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving Changes...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* View Plan Modal */}
      {showViewPlan && viewingPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold text-gray-900">Plan Details</h2>
              <button
                onClick={() => setShowViewPlan(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Tabs Header */}
            <div className="flex border-b border-gray-200 px-6 mt-2">
              <button
                className={`py-2 px-4 border-b-2 font-medium text-sm transition-colors ${activeTab === 'details' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveTab('details')}
              >
                Plan Details
              </button>
              <button
                className={`py-2 px-4 border-b-2 font-medium text-sm transition-colors ${activeTab === 'benefits' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveTab('benefits')}
              >
                Benefits
              </button>
            </div>

            <div className="p-6 space-y-4">
              {activeTab === 'details' ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Plan Name</p>
                      <p className="text-sm font-medium text-gray-900">{viewingPlan.name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Private Office Number</p>
                      <p className="text-sm font-medium text-gray-900">
                        {viewingPlan.rawData?.privateOfficeNo || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Status</p>
                      <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${
                        viewingPlan.status === 'Published' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {viewingPlan.status}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Price</p>
                      <p className="text-sm font-medium text-gray-900">{formatCurrency(viewingPlan.price)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Frequency</p>
                      <p className="text-sm font-medium text-gray-900 capitalize">{viewingPlan.frequency}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Discount</p>
                      <p className="text-sm font-medium text-gray-900">
                        {viewingPlan.discountPercent > 0 ? `${viewingPlan.discountPercent}% OFF` : 'None'}
                      </p>
                    </div>
                  </div>

                  {viewingPlan.description && (
                    <div>
                      <p className="text-xs text-gray-500 mb-2">Description</p>
                      <div 
                        className="prose prose-sm max-w-none p-4 bg-gray-50 rounded-lg border border-gray-100 text-sm"
                        dangerouslySetInnerHTML={{ __html: viewingPlan.description }}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Time Credit */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h3 className="font-semibold text-gray-800 mb-3 text-sm">Time Credit</h3>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="font-medium">Amount: </span> 
                      {viewingPlan.rawData?.benefits?.time_credit?.amount || 0} {viewingPlan.rawData?.benefits?.time_credit?.unit || 'minutes'}
                    </p>
                    <p className="text-sm text-gray-700 mb-2">
                      <span className="font-medium">Expiry: </span> 
                      {viewingPlan.rawData?.benefits?.time_credit?.neverExpire ? 'Never expires' : `${viewingPlan.rawData?.benefits?.time_credit?.expireInDays || 30} days`}
                    </p>
                    <p className="text-xs text-gray-500 mb-1">Applicable Resources:</p>
                    <div className="flex flex-wrap gap-2">
                      {viewingPlan.rawData?.benefits?.time_credit?.applicable_resources?.length > 0 ? (
                        viewingPlan.rawData.benefits.time_credit.applicable_resources.map(id => (
                          <span key={id} className="bg-white border border-gray-200 px-2 py-1 text-xs rounded-md text-gray-600">
                            {getResourceName(id)}
                          </span>
                        ))
                      ) : <span className="text-xs text-gray-400 italic">None selected</span>}
                    </div>
                  </div>

                  {/* Money Credit */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h3 className="font-semibold text-gray-800 mb-3 text-sm">Money Credit</h3>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="font-medium">Amount: </span> 
                      IDR {viewingPlan.rawData?.benefits?.money_credit?.amount || 0}
                    </p>
                    <p className="text-sm text-gray-700 mb-2">
                      <span className="font-medium">Expiry: </span> 
                      {viewingPlan.rawData?.benefits?.money_credit?.neverExpire ? 'Never expires' : `${viewingPlan.rawData?.benefits?.money_credit?.expireInDays || 30} days`}
                    </p>
                    <p className="text-xs text-gray-500 mb-1">Applicable Resources:</p>
                    <div className="flex flex-wrap gap-2">
                      {viewingPlan.rawData?.benefits?.money_credit?.applicable_resources?.length > 0 ? (
                        viewingPlan.rawData.benefits.money_credit.applicable_resources.map(id => (
                          <span key={id} className="bg-white border border-gray-200 px-2 py-1 text-xs rounded-md text-gray-600">
                            {getResourceName(id)}
                          </span>
                        ))
                      ) : <span className="text-xs text-gray-400 italic">None selected</span>}
                    </div>
                  </div>

                  {/* Passes */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h3 className="font-semibold text-gray-800 mb-3 text-sm">Passes</h3>
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Count</p>
                        <p className="text-sm text-gray-700">
                          {viewingPlan.rawData?.benefits?.passes?.is_unlimited ? 'Unlimited' : (viewingPlan.rawData?.benefits?.passes?.count || 0)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Expiry</p>
                        <p className="text-sm text-gray-700">
                          {viewingPlan.rawData?.benefits?.passes?.neverExpire ? 'Never expires' : `${viewingPlan.rawData?.benefits?.passes?.expireInDays || 30} days`}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-xs text-gray-500 mb-1">Access Window</p>
                        <p className="text-sm text-gray-700">
                          {viewingPlan.rawData?.benefits?.passes?.access_window?.start_time || '--:--'} to {viewingPlan.rawData?.benefits?.passes?.access_window?.end_time || '--:--'}
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mb-1">Valid Days:</p>
                    <div className="flex flex-wrap gap-2">
                      {viewingPlan.rawData?.benefits?.passes?.access_window?.days?.length > 0 ? (
                        viewingPlan.rawData.benefits.passes.access_window.days.map(day => (
                          <span key={day} className="bg-white border border-gray-200 px-2 py-1 text-xs rounded-md text-gray-600">
                            {day}
                          </span>
                        ))
                      ) : <span className="text-xs text-gray-400 italic">None selected</span>}
                    </div>
                  </div>

                  {/* Printing Credits */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h3 className="font-semibold text-gray-800 mb-3 text-sm">Printing Credits</h3>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="font-medium">Amount: </span> 
                      {/* Check if it's an object or an old plain number */}
                      {typeof viewingPlan.rawData?.benefits?.printing_credits === 'object' 
                         ? viewingPlan.rawData?.benefits?.printing_credits?.amount 
                         : (viewingPlan.rawData?.benefits?.printing_credits || 0)} pages
                    </p>
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Expiry: </span> 
                      {viewingPlan.rawData?.benefits?.printing_credits?.neverExpire ? 'Never expires' : `${viewingPlan.rawData?.benefits?.printing_credits?.expireInDays || 30} days`}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 sticky bottom-0 bg-white">
              <button
                onClick={() => setShowViewPlan(false)}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {isOverlayOpen && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in">
          <div className="bg-gray-900 text-white px-6 py-3 rounded-lg shadow-lg">
            <p className="text-sm">{overlayMessage}</p>
          </div>
        </div>
      )}
    </div>
  );
}